/**
 * Environment und URL Utilities für die Mietwerkstatt
 * Zentrale Stelle für alle Environment-spezifischen Konfigurationen
 */

// Environment Detection
export const isDevelopment = process.env.NODE_ENV === "development";
export const isProduction = process.env.NODE_ENV === "production";

// Base URLs
export const getApiBaseUrl = (): string => {
  // In Development: Über Proxy oder direkter API-Pfad
  if (isDevelopment) {
    return process.env.REACT_APP_API_URL || "/api";
  }

  // In Production: Immer über /api (Nginx Route)
  return "/api";
};

// Rechnungs-URLs für QR-Codes und Links
export const getPosBaseUrl = (): string => {
  // Custom Environment Variable für Rechnungs-/POS-Domain
  const customPosDomain = process.env.REACT_APP_POS_DOMAIN;

  if (customPosDomain) {
    return customPosDomain.startsWith("http")
      ? customPosDomain
      : `https://${customPosDomain}`;
  }

  // Development: /pos Pfad
  if (isDevelopment) {
    return `${window.location.origin}/pos`;
  }

  // Production: Rechnungen laufen auf einer eigenen öffentlichen Subdomain.
  const { protocol, hostname } = window.location;
  const posHostname = hostname.startsWith("pos.")
    ? hostname
    : hostname.startsWith("rechnungen.")
    ? hostname
    : hostname.startsWith("vermietung.")
    ? hostname.replace(/^vermietung\./, "rechnungen.")
    : `rechnungen.${hostname}`;

  return `${protocol}//${posHostname}`;
};

// URL Builders für verschiedene Zwecke
export const buildInvoiceUrl = (qrCode: string): string => {
  const posBase = getPosBaseUrl();
  return `${posBase}/invoice/${qrCode}?format=html`;
};

export const buildRentalUrl = (rentalId: any): string => {
  const posBase = getPosBaseUrl();
  return `${posBase}/rental/${rentalId}`;
};

export const buildPdfDownloadUrl = (qrCode: string): string => {
  // In Development: Direkte PDF-URL über /pos (wird von setupProxy.js weitergeleitet)
  if (isDevelopment) {
    return `${window.location.origin}/pos/invoice/${qrCode}`;
  }

  // In Production: POS-Domain für direkten PDF-Download
  const posBase = getPosBaseUrl();
  return `${posBase}/invoice/${qrCode}`;
};

// Neue Funktion: QR-Code URL für direkte PDF-Downloads (öffentlich zugänglich)
export const buildPublicPdfUrl = (qrCode: string): string => {
  return buildPdfDownloadUrl(qrCode);
};

// Neue Funktion: Authentifizierte PDF-Download URL (über API)
export const buildAuthenticatedPdfUrl = (qrCode: string): string => {
  const apiBase = getApiBaseUrl();

  // Wenn apiBase bereits absolut ist (Development), direkt verwenden
  if (apiBase.startsWith("http")) {
    return `${apiBase}/invoices/public/${qrCode}/download`;
  }

  // Ansonsten relativen Pfad verwenden (Production über Nginx)
  return `${apiBase}/invoices/public/${qrCode}/download`;
};

// Debug-Informationen für Entwicklung
export const getEnvironmentInfo = () => {
  return {
    environment: process.env.NODE_ENV,
    isDevelopment,
    isProduction,
    apiBaseUrl: getApiBaseUrl(),
    posBaseUrl: getPosBaseUrl(),
    currentOrigin: window.location.origin,
    currentHostname: window.location.hostname,
    customPosDomain: process.env.REACT_APP_POS_DOMAIN || "not set",
  };
};

// Logging für Debugging
if (isDevelopment) {
  console.log("🌍 Environment Info:", getEnvironmentInfo());
}
